import { EMPTY_LOCALE_EVENT_KEY, EventDelegator, EventMapper } from '@philips-internal/oswald-ui-core/lib/component/event/index.js';
import { eventListener } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { LocaleService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { scopedErrorMessage } from '@philips-internal/oswald-ui-utils';
const lifecycleEvents = ['onMount', 'onUnmount'];
// To correctly type this would send us in a giant rabbit hole, as it seems that every other class depends on the ComponentInfo
// class, and in turn these classes are used by others and so on, so we would have to add Generics to basically every class used
// by the core package and a lot of classes by the engine package, increasing the complexity of the codebase _a whole_ by a lot.
// To simplify this we would need to refactor _a lot_ of the core and engine codebase, simplifying it as we go.
/**
 * Engine's EventDelegator, responsible to gather events from React API and DI Container.
 * The fire order is:
 * - Callback comming from React parent node;
 * - Callbacks binded by API;
 * - Callbacks binded by DI Container.
 */
// @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
class EngineEventDelegator extends EventDelegator {
    constructor(context) {
        super();
        this.props = {};
        this.context = context;
        const propTypes = context.getPropTypes();
        const eventProps = Object.keys(propTypes).filter(p => propTypes[p] === eventListener);
        eventProps.concat(lifecycleEvents).forEach(event => {
            this.events[event] = this.generate(event);
        });
    }
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    generate(event) {
        return (...args) => {
            const { context } = this;
            const api = context.getAPI();
            const owner = context.getOwner();
            const container = owner.getContainer();
            try {
                // first check parent callback
                const callback = this.props[event];
                callback && callback(...args);
            }
            catch (e) {
                const c = console;
                c.log('[Engine]: Problem to invoke callback', e);
            }
            try {
                const eventListener = api && api[event];
                if (eventListener) {
                    try {
                        const eventData = eventListener.prepare(...args);
                        // second check handler's callbacks
                        const eventsByBus = this.eventBus.get(event);
                        eventsByBus && eventsByBus.forEach((callback) => callback(eventData));
                        // third check controller's callbacks
                        if (container) {
                            const alias = context.getSSOT().state.alias;
                            if (alias) {
                                const component = EventMapper.getComponent(owner.getName(), alias);
                                if (!component) {
                                    return;
                                }
                                const address = this.getEventAddress(component, event);
                                if (address) {
                                    const { className, methodName } = address;
                                    const instance = container.provide(className, true);
                                    if (instance) {
                                        instance[methodName](eventData);
                                    }
                                }
                            }
                        }
                    }
                    catch (e) {
                        scopedErrorMessage('engine', 'Error while invoking callback:', e);
                    }
                }
            }
            catch {
                // Empty catch blocks to prevent proxy errors while components are getting created/destroyed by the engine.
                // Problem may occur when component are created, call `setState` and within its callback function
                // try to trigger an event.
                // Only occurs on components with children that are destroyed instead of updated.
            }
        };
    }
    /**
     * Get the address that should be called for a component, given the event type.
     *
     * @param component - Map of all events associated with a component.
     * @param event - The event being triggered.
     */
    getEventAddress(component, event) {
        const eventMap = component.get(event);
        const currentLocaleEvent = eventMap?.get(LocaleService.getCurrentLocale());
        const withoutLocaleEvent = eventMap?.get(EMPTY_LOCALE_EVENT_KEY);
        return currentLocaleEvent ?? withoutLocaleEvent;
    }
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    fire(type, event) {
        const callback = this.events[type];
        if (callback) {
            // @ts-ignore
            callback(event);
        }
    }
    /**
     * Retrieves all event's callbacks.
     * It needs to keep the current props comming form parent, to capture possible event callbacks.
     *
     * @param props the props comming from parent.
     * @returns the events callbacks.
     */
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    collect(props) {
        this.props = props;
        return this.events;
    }
}
export { EngineEventDelegator, };
//# sourceMappingURL=engine-event-delegator.js.map